/**
 * package-fields.js
 *
 * Single source of truth for package_details API response property names.
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * The backend uses Sequelize with `underscored: true`, which means the database
 * columns are snake_case (e.g. `created_at`) but the JavaScript object properties
 * returned by the API are the Sequelize JS field names:
 *   - Auto-managed timestamps  → camelCase:  `createdAt`, `updatedAt`
 *   - Explicitly-named columns → snake_case:  `direction_type`, `address_list`, etc.
 *
 * Always reference property names through this file to avoid silent undefined/N-A bugs.
 *
 * USAGE
 * -----
 *   import { PKG, formatPkgDate, formatPkgAddresses } from '../../utils/package-fields';
 *   pkg[PKG.CREATED_AT]       // 'createdAt'
 *   formatPkgDate(pkg)        // '5/6/2026, 1:00:00 AM'
 *   formatPkgAddresses(pkg)   // ['123 Main St', '456 Oak Ave']
 */

/** Canonical JS property names on a package_details API response object */
export const PKG = {
  // Auto-managed Sequelize timestamps (always camelCase regardless of underscored:true)
  CREATED_AT: 'createdAt',
  UPDATED_AT: 'updatedAt',

  // Explicit model fields (snake_case, matching the model definition)
  ID: 'id',
  USER_ID: 'user_id',
  TRACKING_NUMBER: 'tracking_number',
  COMPANY_NAME: 'company_name',
  ORDER_NUMBER: 'order_number',
  BATCH_NAME: 'batch_name',
  DIRECTION_TYPE: 'direction_type',
  RECIPIENT_NAME: 'recipient_name',
  BARCODE_LIST: 'barcode_list',
  ADDRESS_LIST: 'address_list',
  IMAGE_PATH_LIST: 'image_path_list',
  VIDEO_PATH_LIST: 'video_path_list',
  OTHER_DETAILS: 'other_details',
  SHIPPING_LABEL_PHOTOS: 'shipping_label_photos',
  PACKAGE_PHOTOS: 'package_photos',
  ITEMS: 'items',
  SCANNED_AT_LATITUDE: 'scanned_at_latitude',
  SCANNED_AT_LONGITUDE: 'scanned_at_longitude',
  SCANNED_AT_ADDRESS: 'scanned_at_address',
  MATCHED_ADDRESS_ID: 'matched_address_id',
  REMARKS: 'remarks',
  IS_DUPLICATE: 'is_duplicate',
  ZOE_SHIPMENT_TRACKING_ID: 'zoe_shipment_tracking_id',
  ZOE_SHIPMENT_TRACKING_LOG_ID: 'zoe_shipment_tracking_log_id',
};

/**
 * Format the package's date collected (createdAt) for display.
 * Returns 'N/A' if the value is missing.
 * @param {object} pkg - Package object from the API
 * @returns {string}
 */
export const formatPkgDate = (pkg) => {
  const val = pkg?.[PKG.CREATED_AT];
  if (!val) return 'N/A';
  return new Date(val).toLocaleString();
};

/**
 * Format the package's address_list for display.
 * Handles both string entries and object entries.
 * Returns a flat array of display strings (or ['N/A'] if empty).
 * @param {object} pkg - Package object from the API
 * @returns {string[]}
 */
export const formatPkgAddresses = (pkg) => {
  const list = pkg?.[PKG.ADDRESS_LIST];
  if (!list || !Array.isArray(list) || list.length === 0) return ['N/A'];
  return list.map((addr) =>
    typeof addr === 'object' ? JSON.stringify(addr) : String(addr)
  );
};
